import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { API_URL } from '../services/api';
import { useAuth } from '../context/AuthContext';

interface ReportProblemModalProps {
    open: boolean;
    onClose: () => void;
}

const ReportProblemModal: React.FC<ReportProblemModalProps> = ({ open, onClose }) => {
    const { token } = useAuth();
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState('');

    if (!open) return null;

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        try {
            const headers: HeadersInit = { 'Content-Type': 'application/json' };
            if (token) headers.Authorization = `Bearer ${token}`;
            const res = await fetch(`${API_URL}/support`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ subject, body: message, source: 'report' }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || 'Envoi impossible');
            setDone(true);
            setSubject('');
            setMessage('');
        } catch (err: any) {
            setError(err.message || 'Erreur');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute right-3 top-3 text-slate-400 hover:text-slate-700"
                >
                    <X className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
                        <AlertTriangle className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className="text-lg font-black text-slate-900">Signaler un problème</h3>
                        <p className="text-xs text-slate-500">Notre équipe vous répondra rapidement</p>
                    </div>
                </div>

                {done ? (
                    <div className="space-y-4">
                        <p className="text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2 font-semibold">
                            Message envoyé. La réponse apparaîtra ici en notification.
                        </p>
                        <button
                            type="button"
                            onClick={onClose}
                            className="w-full bg-indigo-600 text-white font-bold rounded-xl py-2.5"
                        >
                            Fermer
                        </button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-3">
                        <input
                            required
                            className="w-full border rounded-xl px-3 py-2 text-sm"
                            placeholder="Sujet"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                        />
                        <textarea
                            required
                            className="w-full border rounded-xl px-3 py-2 text-sm min-h-[120px]"
                            placeholder="Décrivez le problème…"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                        />
                        {error && (
                            <p className="text-sm text-rose-600 font-semibold">{error}</p>
                        )}
                        <button
                            type="submit"
                            disabled={busy}
                            className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-bold rounded-xl py-2.5"
                        >
                            {busy ? 'Envoi…' : 'Envoyer'}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default ReportProblemModal;
